import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import type { SortDirection } from '@/api/gridTypes';

import { cx } from './classNames';
import { SimpleTableCell } from './SimpleTableCell';
import { SimpleTableColGroup, SimpleTableHeader } from './SimpleTableHeader';
import { SimpleTablePager } from './SimpleTablePager';
import { SimpleTableToolbar } from './SimpleTableToolbar';
import { filterAndSortRows, getFieldValue, toText } from './simpleTableUtils';

/**
 * Spalte der {@link SimpleTable} — Pendant zu `GridColumn`.
 *
 * @property field         Feldname der Zeile (dynamischer Zugriff wie `GridColumn.Field`).
 * @property headerText    Überschrift; fehlt sie, wird `field` angezeigt.
 * @property visible       Standard `true`; unsichtbare Spalten bleiben für Suche und Schlüssel erhalten.
 * @property isPrimaryKey  Schlüsselspalte — bestimmt Zeilenidentität, Auswahl und Bearbeitung.
 * @property allowEditing  Erlaubt Batch-Zellbearbeitung per Doppelklick.
 * @property width         Spaltenbreite in Pixeln.
 * @property template      Eigene Zellanzeige, z. B. Kommandospalte oder formatiertes Datum.
 * @property editor        Eigener Editor; `commit(updated)` schließt ihn und meldet `onCellSave`.
 */
export interface SimpleTableColumn<T> {
  field: string;
  headerText?: string;
  visible?: boolean;
  isPrimaryKey?: boolean;
  allowEditing?: boolean;
  width?: number;
  template?: (row: T) => ReactNode;
  editor?: (row: T, commit: (updated: T) => void) => ReactNode;
}

export type SimpleTableToolbarItem = 'Search' | 'Add' | 'Delete';

/** Wortlaut von Syncfusion (`GridEmptyRecordTemplate`). */
const NO_RECORDS = 'No records to display';

/**
 * Schlanke, clientseitige Tabelle mit den `e-grid`-Klassen — Ersatz für alle
 * `SfGrid`-Verwendungen **ohne** serverseitige Datenquelle (Audit-Liste,
 * Dokumentliste, sämtliche Administrationslisten).
 *
 * @property dataSource        Vollständige Zeilenliste; Suche, Sortierung und Paging laufen clientseitig.
 * @property columns           Spaltendefinition, siehe {@link SimpleTableColumn}.
 * @property toolbar           Sichtbare Werkzeuge, z. B. `['Add', 'Delete', 'Search']`.
 * @property defaultSort       Startsortierung, z. B. `{ field: 'DateTime', direction: 'descending' }`.
 * @property pageSize          Zeilen je Seite; Standard 12 (Syncfusion-Vorgabe).
 * @property allowPaging       Standard `true`.
 * @property selectionMode     `Single` (Standard) oder `Multiple` für die Auswahldialoge.
 * @property onSelectionChange Meldet die aktuell ausgewählten Zeilen.
 * @property onAdd             Rückruf der Schaltfläche `Add`.
 * @property onDelete          Rückruf der Schaltfläche `Delete` mit den ausgewählten Zeilen.
 * @property onCellSave        Wird nach Bestätigen einer Zelle mit der geänderten Zeile und dem Feldnamen aufgerufen.
 */
export interface SimpleTableProps<T> {
  dataSource: T[];
  columns: SimpleTableColumn<T>[];
  toolbar?: SimpleTableToolbarItem[];
  defaultSort?: { field: string; direction: SortDirection };
  pageSize?: number;
  allowPaging?: boolean;
  selectionMode?: 'Single' | 'Multiple';
  onSelectionChange?: (rows: T[]) => void;
  onAdd?: () => void;
  onDelete?: (rows: T[]) => void;
  onCellSave?: (row: T, field: string) => void;
  id?: string;
  className?: string;
  style?: CSSProperties;
}

export function SimpleTable<T>(props: SimpleTableProps<T>) {
  const { columns, dataSource, onCellSave, onSelectionChange } = props;
  const toolbar = props.toolbar ?? [];
  const pageSize = props.pageSize ?? 12;

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(props.defaultSort ?? null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ key: string; field: string } | null>(null);

  const keyField = columns.find((column) => column.isPrimaryKey === true)?.field;
  const visibleColumns = columns.filter((column) => column.visible !== false);
  const keyOf = (row: T, index: number) =>
    keyField === undefined ? String(index) : toText(getFieldValue(row, keyField));

  const rows = useMemo(
    () =>
      filterAndSortRows(
        dataSource,
        columns.map((column) => column.field),
        search,
        sort,
      ),
    [columns, dataSource, search, sort],
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount);
  const paged =
    props.allowPaging === false ? rows : rows.slice((current - 1) * pageSize, current * pageSize);

  const selectedRows = (keys: string[]) =>
    rows.filter((row, index) => keys.includes(keyOf(row, index)));

  const select = (key: string) => {
    const multiple = props.selectionMode === 'Multiple';
    const keys = selectedKeys.includes(key)
      ? multiple
        ? selectedKeys.filter((entry) => entry !== key)
        : []
      : multiple
        ? [...selectedKeys, key]
        : [key];
    setSelectedKeys(keys);
    onSelectionChange?.(selectedRows(keys));
  };

  const toggleSort = (field: string) => {
    setSort((state) => {
      if (state?.field !== field) return { field, direction: 'ascending' };
      return state.direction === 'ascending' ? { field, direction: 'descending' } : null;
    });
  };

  return (
    <div id={props.id} style={props.style} className={cx('e-grid', 'e-gridhover', props.className)}>
      {toolbar.length === 0 ? null : (
        <SimpleTableToolbar
          showSearch={toolbar.includes('Search')}
          showAdd={toolbar.includes('Add')}
          showDelete={toolbar.includes('Delete')}
          search={search}
          onSearchChange={(text) => {
            setSearch(text);
            setPage(1);
          }}
          onAdd={props.onAdd}
          onDelete={() => {
            props.onDelete?.(selectedRows(selectedKeys));
          }}
        />
      )}
      <SimpleTableHeader columns={visibleColumns} sort={sort} onToggleSort={toggleSort} />
      <div className="e-gridcontent">
        <table className="e-table">
          <SimpleTableColGroup columns={visibleColumns} />
          <tbody>
            {paged.length === 0 ? (
              <tr className="e-emptyrow">
                <td colSpan={visibleColumns.length}>{NO_RECORDS}</td>
              </tr>
            ) : null}
            {paged.map((row, index) => {
              const key = keyOf(row, index);
              return (
                <tr key={key} className={cx('e-row', index % 2 === 1 && 'e-altrow')}>
                  {visibleColumns.map((column) => (
                    <SimpleTableCell
                      key={column.field}
                      row={row}
                      column={column}
                      editable={column.allowEditing === true && onCellSave !== undefined}
                      editing={editing?.key === key && editing.field === column.field}
                      selected={selectedKeys.includes(key)}
                      onSelect={() => {
                        select(key);
                      }}
                      onStartEdit={() => {
                        setEditing({ key, field: column.field });
                      }}
                      onCancelEdit={() => {
                        setEditing(null);
                      }}
                      onCommit={(updated) => {
                        setEditing(null);
                        onCellSave?.(updated, column.field);
                      }}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {props.allowPaging === false ? null : (
        <SimpleTablePager
          page={current}
          pageCount={pageCount}
          totalCount={rows.length}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

export default SimpleTable;
